import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, unwrap } from "./api";
export { hasAnyRole, isStaff, primaryRole } from "./rolePolicy";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("eduos_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = unwrap(await api.get("/me"));
      setUser(me);
      // Refresh tenant currency in case it changed since login
      if (me?.tenant) {
        localStorage.setItem("eduos_tenant_currency", JSON.stringify({
          symbol: me.tenant.currency_symbol || "₹",
          code: me.tenant.currency_code || "INR",
          locale: me.tenant.locale || "en-IN",
          country: me.tenant.country || "IN",
        }));
      }
    } catch (_e) {
      localStorage.removeItem("eduos_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const data = unwrap(await api.post("/auth/login", { email, password }));
    localStorage.setItem("eduos_token", data.access_token);
    // Persist tenant currency so formatMoney/formatDate can localize (INR or AED)
    if (data.tenant) {
      localStorage.setItem("eduos_tenant_currency", JSON.stringify({
        symbol: data.tenant.currency_symbol || "₹",
        code: data.tenant.currency_code || "INR",
        locale: data.tenant.locale || "en-IN",
        country: data.tenant.country || "IN",
      }));
    }
    await refresh();
    return data;
  };

  const logout = () => {
    localStorage.removeItem("eduos_token");
    localStorage.removeItem("eduos_tenant_currency");
    setUser(null);
    window.location.href = "/login";
  };

  const switchContext = async (institutionId) => {
    const data = unwrap(await api.post("/me/switch-context", { institution_id: institutionId }));
    localStorage.setItem("eduos_token", data.access_token);
    await refresh();
    return data;
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh, switchContext }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
